import React from 'react';
import { am5, am5themes_Animated, am5xy } from '@local/finsera-core/src/libs';
import * as types from '@local/finsera-core/src/types';

import { createAxes } from './methods/create-axes';
import { createSeries } from './methods/create-series';
import { createBaseLegend } from '../shared/create-base-legend';
import { createDownloadButton } from '../shared/create-download-button';
import { handleZoom } from '../shared/handle-zoom';

export type ChartProps = {
  id: string;
  data: types.widgets.TableData;
  fullScreen: boolean;

  xAxisField: string;
  innerBar: string;
  innerBarTitle: string;
  outerBar: string;
  outerBarTitle: string;
  line: string;
  lineTitle: string;

  leftTitle: string;
  rightTitle: string;

  download: boolean;
  customDownloadFormatting: (_data: types.widgets.TableData) => types.widgets.TableData;
};

const Chart = ({
  id,
  data,
  fullScreen,
  xAxisField,
  download,
  leftTitle,
  rightTitle,
  innerBar,
  innerBarTitle,
  outerBar,
  outerBarTitle,
  line,
  lineTitle,
  customDownloadFormatting,
}: ChartProps) => {
  const widgetId = `chart-${id}-${fullScreen ? 'fs' : 'sw'}`;
  const dataLength = Object.keys(data?.[0] ?? {}).length;
  const shouldShowLegend = dataLength > 1 && (fullScreen || dataLength <= 6);

  React.useEffect(() => {
    // Create root element
    const root = am5.Root.new(widgetId);

    // Remove amCharts logo
    root._logo?.dispose();

    // Set themes
    root.setThemes([am5themes_Animated.new(root)]);

    // Create chart
    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
        wheelX: 'panX',
        wheelY: 'zoomX',
        pinchZoomX: false,
        layout: root.verticalLayout,
        paddingLeft: download ? 20 : 0,
        paddingBottom: shouldShowLegend ? 0 : 20,
      })
    );

    // Root settings
    chart.appear(1000, 100);

    const { categoryAxis, valueAxis1, valueAxis2 } = createAxes({
      root,
      chart,
      data,
      xAxisField,
      leftTitle,
      rightTitle,
    });

    createSeries({
      root,
      chart,
      data,
      categoryAxis,
      valueAxis1,
      valueAxis2,
      xAxisField,
      innerBar,
      innerBarTitle,
      outerBar,
      outerBarTitle,
      line,
      lineTitle,
      valueAxis: valueAxis2,
    });

    chart.set('cursor', am5xy.XYCursor.new(root, {}));

    if (shouldShowLegend) createBaseLegend({ root, chart, yAxis: valueAxis1, fullScreen });
    handleZoom(chart);
    if (download) createDownloadButton(root, xAxisField, data, customDownloadFormatting);

    return () => {
      root.dispose();
    };
  }, [data]);

  return <div id={widgetId} style={{ width: '100%', height: '100%' }} />;
};

export default Chart;
